// Carrier errors are stored on the activity log as
// `params: { carrierCode, category, errorCode, rawMessage }`. `category` maps to
// a translatable merchant message; `errorCode` is the carrier's own code, kept
// for support and per-code aggregation; `rawMessage` is the untouched payload.
type CarrierErrorParams = {
  carrierCode: string;
  category: string;
  rawMessage: string;
};

function getCarrierErrorParams(params: unknown): CarrierErrorParams | null {
  if (
    params === null ||
    typeof params !== "object" ||
    !("category" in params) ||
    !("rawMessage" in params)
  ) {
    return null;
  }

  const record = params as Record<string, unknown>;
  if (
    typeof record.category !== "string" ||
    typeof record.rawMessage !== "string"
  ) {
    return null;
  }

  return {
    carrierCode:
      typeof record.carrierCode === "string" ? record.carrierCode : "",
    category: record.category,
    rawMessage: record.rawMessage,
  };
}

const CATEGORY_TRANSLATION_KEY: Record<string, string> = {
  "order-state": "errors.carrier.orderState",
  "network-unavailable": "errors.carrier.network",
  auth: "errors.carrier.auth",
  "carrier-config": "errors.carrier.carrierConfig",
  "carrier-internal": "errors.carrier.carrierInternal",
  "address-recipient": "errors.carrier.addressRecipient",
  "parcel-data": "errors.carrier.parcelData",
};

// null for unknown/unmapped categories — the caller falls back to the raw message.
function getCarrierErrorTranslationKey(category: string): string | null {
  return CATEGORY_TRANSLATION_KEY[category] ?? null;
}

type ResolvedCarrierError = {
  // Merchant-facing message: the translated category, or the raw payload when
  // the category is unknown, or the legacy fallback for logs without params.
  message: string | null | undefined;
  // The untouched carrier payload, kept for support. `null` when there is
  // nothing extra worth showing — legacy logs, or when `message` already is
  // the raw payload (unknown category).
  raw: string | null;
};

// Splits a carrier-error log into the message to show the merchant and the raw
// payload to expose separately (e.g. behind a collapsible). `legacyFallback`
// covers old logs written before structured params existed.
export function resolveCarrierError(
  params: unknown,
  t: (key: string) => string,
  legacyFallback: () => string | null | undefined,
): ResolvedCarrierError {
  const carrierParams = getCarrierErrorParams(params);
  if (carrierParams) {
    const key = getCarrierErrorTranslationKey(carrierParams.category);
    return {
      message: key ? t(key) : carrierParams.rawMessage,
      raw: key ? carrierParams.rawMessage : null,
    };
  }

  return { message: legacyFallback(), raw: null };
}

// Convenience wrapper for call sites that only need the display message.
export function resolveCarrierErrorMessage(
  params: unknown,
  t: (key: string) => string,
  legacyFallback: () => string | null | undefined,
): string | null | undefined {
  return resolveCarrierError(params, t, legacyFallback).message;
}
